import type { CommandReceipt } from "./domain/session.ts";

/** Lifecycle actions understood by the Daemon Agent manager. */
export type AgentOperationKind = "install" | "uninstall" | "upgrade" | "enable" | "disable";

export interface AgentOperationInput {
  agentId: string;
  operation: AgentOperationKind;
  expectedRevision?: number;
  /** Stable idempotency identity. Omit to let the transport mint one. */
  operationId?: string;
}

/**
 * Durable, typed projection of one Agent lifecycle command.
 *
 * `status` remains compatible with the shared CommandReceipt surface. The
 * additional fields preserve the Daemon receipt metadata needed to decide
 * whether retrying is safe after a failure or an interrupted Daemon process.
 */
export interface AgentOperationReceipt extends CommandReceipt {
  operationId: string;
  commandId: string;
  receiptId: string | null;
  operation: AgentOperationKind;
  targetDeviceId: string | null;
  phase: "queued" | "accepted" | "running" | "completed" | "failed" | "rejected" | "interrupted" | "expired" | "cancelled";
  entityRevision: number | null;
  retryable: boolean;
  errorCode: string | null;
  /** False only when the authority cannot prove whether the host changed. */
  completionKnown: boolean;
  /** Cancelling a poll never changes the lifecycle command itself. */
  observation: "active" | "cancelled" | "complete";
}

/** Options for observing a Daemon-owned Agent lifecycle operation. */
export interface AgentOperationPollOptions {
  /** Maximum time to wait before returning the latest non-terminal receipt. */
  timeoutMs?: number;
  /** Poll interval used by transports without a receipt stream. */
  intervalMs?: number;
  signal?: AbortSignal;
}

/** Return the wire operation name used by the shared command envelope. */
export function agentOperationName(operation: AgentOperationKind): CommandReceipt["command"] {
  return `agents.${operation}` as CommandReceipt["command"];
}
